import { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { DOC_SECTIONS } from "@/data/docs";

const SectionBlock = ({ block }) => {
  if (block.type === "list") {
    return (
      <ul className="mt-4 space-y-2">
        {block.items.map((item, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed text-paper-400 md:text-base">
            <span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-white/50" aria-hidden="true" />
            <span className={item.startsWith("/") ? "font-mono text-[13px] text-paper-200" : ""}>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="mt-4 text-sm leading-relaxed text-paper-400 md:text-base">{block.text}</p>;
};

export default function Docs() {
  useSEO({
    title: "Documentation",
    description: "Xevon documentation — installation, permissions, commands, and configuration for every module.",
    path: "/docs",
  });

  const [active, setActive] = useState(DOC_SECTIONS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    DOC_SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const activeIndex = DOC_SECTIONS.findIndex((s) => s.id === active);
  const activeSection = DOC_SECTIONS[activeIndex] || DOC_SECTIONS[0];
  const prev = DOC_SECTIONS[activeIndex - 1];
  const next = DOC_SECTIONS[activeIndex + 1];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <PageHeader
        kicker="Documentation"
        title="Set up Xevon in minutes."
        description="From invite to full configuration — installation, permissions, and every module explained."
      />
      <div className="mx-auto w-full max-w-7xl px-6 pb-32" data-testid="docs-page">
        <nav className="mb-6 flex items-center gap-2 font-mono text-xs text-paper-500" aria-label="Breadcrumb" data-testid="docs-breadcrumb">
          <span>Docs</span>
          <span aria-hidden="true">/</span>
          <span className="text-paper-200">{activeSection.title}</span>
        </nav>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-12 lg:grid-cols-[240px_minmax(0,1fr)]">
          <aside className="min-w-0 lg:sticky lg:top-28 lg:self-start">
            <div className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-paper-500">
              <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
              Contents
            </div>
            <nav
              className="flex w-full max-w-full gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-1 lg:overflow-visible lg:pb-0"
              aria-label="Documentation sections"
              data-testid="docs-sidebar"
            >
              {DOC_SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  data-testid={`docs-nav-${s.id}`}
                  aria-current={active === s.id ? "true" : undefined}
                  className={`whitespace-nowrap rounded-lg px-4 py-2 text-left text-sm transition-colors duration-300 lg:whitespace-normal ${
                    active === s.id
                      ? "bg-white/10 font-semibold text-paper-100"
                      : "text-paper-500 hover:bg-white/5 hover:text-paper-200"
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </nav>
          </aside>
          <div className="min-w-0">
            {DOC_SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-32 border-b border-white/5 pb-14 pt-4 first:pt-0 mb-14" data-testid={`docs-section-${s.id}`}>
                <Reveal>
                  <h2 className="font-display text-2xl font-semibold tracking-tight text-paper-100 md:text-3xl">
                    {s.title}
                  </h2>
                  {s.blocks.map((block, i) => (
                    <SectionBlock key={i} block={block} />
                  ))}
                </Reveal>
              </section>
            ))}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {prev ? (
                <button
                  onClick={() => scrollTo(prev.id)}
                  data-testid="docs-prev-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-paper-200 transition-colors duration-300 hover:bg-white/10"
                >
                  <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" aria-hidden="true" />
                  {prev.title}
                </button>
              ) : (
                <span />
              )}
              {next && (
                <button
                  onClick={() => scrollTo(next.id)}
                  data-testid="docs-next-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-paper-200 transition-colors duration-300 hover:bg-white/10"
                >
                  {next.title}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
